import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  readAdminCustomerRouteParam,
  readRouteParam,
} from '../../../_utils/route-param.utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import ICalendar from '../../../_interfaces/ICalendar';
import ISite from '../../../_interfaces/ISite';
import { AdminEntityLoaderService } from '../../../_services/admin/admin-entity-loader.service';
import { CalendarService } from '../../../_services/calendars/calendar.service';
import { AdminFormLayoutComponent } from '../../../components/admin-ui/admin-form-layout/admin-form-layout.component';
import { AdminPageHeaderComponent } from '../../../components/admin-ui/admin-page-header/admin-page-header.component';
import { AdminStatePanelComponent } from '../../../components/admin-ui/admin-state-panel/admin-state-panel.component';
import { FormCalendarComponent } from '../../../components/forms/form-calendar/form-calendar.component';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-admin-calendar-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AdminPageHeaderComponent,
    AdminStatePanelComponent,
    AdminFormLayoutComponent,
    FormCalendarComponent,
    ButtonModule,
    FormsModule,
  ],
  templateUrl: './admin-calendar-detail.component.html',
  styleUrl: './admin-calendar-detail.component.scss',
})
export class AdminCalendarDetailComponent implements OnInit {
  public customersId = 0;
  public sitesId = 0;
  public calendarId = 0;
  public site: ISite | null = null;
  public calendar: ICalendar | null = null;
  public loading = true;
  public hasError = false;
  public activeSection: 'details' | 'schedule' = 'details';

  public readonly weekDays = [
    { id: 1, labelKey: 'COMMON.WEEKDAY.MONDAY' },
    { id: 2, labelKey: 'COMMON.WEEKDAY.TUESDAY' },
    { id: 3, labelKey: 'COMMON.WEEKDAY.WEDNESDAY' },
    { id: 4, labelKey: 'COMMON.WEEKDAY.THURSDAY' },
    { id: 5, labelKey: 'COMMON.WEEKDAY.FRIDAY' },
    { id: 6, labelKey: 'COMMON.WEEKDAY.SATURDAY' },
    { id: 7, labelKey: 'COMMON.WEEKDAY.SUNDAY' },
  ];

  public scheduleDraft: Record<number, Array<{ start: string; end: string }>> =
    this.createEmptyScheduleDraft();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly loader: AdminEntityLoaderService,
    private readonly calendarService: CalendarService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
  ) {}

  get backLink(): any[] {
    return [
      '/administration',
      readAdminCustomerRouteParam(this.route, this.customersId) || this.customersId,
      'clinics',
      this.sitesId,
      'calendars',
    ];
  }

  get isEditMode(): boolean {
    return !!this.calendarId;
  }

  readonly onCreate = (): void => {
    this.router.navigate(this.backLink);
  };

  readonly onUpdate = (): void => {
    this.load();
  };

  ngOnInit(): void {
    this.customersId = readRouteParam(this.route, 'customers_id');
    this.sitesId = readRouteParam(this.route, 'sites_id');
    this.calendarId = readRouteParam(this.route, 'calendar_id');
    this.load(true);
  }

  load(fromResolver = false): void {
    this.loading = true;
    this.hasError = false;

    const resolvedSite = (this.route.snapshot.data['site'] || null) as
      | ISite
      | null;
    this.site = resolvedSite;

    if (!this.site) {
      this.hasError = true;
      this.loading = false;
      return;
    }

    if (!this.isEditMode) {
      this.calendar = null;
      this.hydrateScheduleDraft();
      this.loading = false;
      return;
    }

    if (fromResolver) {
      const calendar = (this.route.snapshot.data['calendar'] || null) as
        | ICalendar
        | null;
      if (calendar) {
        this.calendar = calendar;
        this.loadTimetables();
        return;
      }
    }

    this.loader.loadCalendar(this.customersId, this.sitesId, this.calendarId).subscribe({
      next: (calendar) => {
        this.calendar = calendar;
        this.hasError = !calendar;
        this.loadTimetables();
      },
      error: () => {
        this.hasError = true;
        this.loading = false;
      },
    });
  }

  setSection(section: 'details' | 'schedule'): void {
    this.activeSection = section;
  }

  getRangesForDay(day: number): Array<{ start: string; end: string }> {
    return this.scheduleDraft[day] || [];
  }

  addRange(day: number): void {
    const ranges = this.getRangesForDay(day);
    const fallbackStart = ranges.length ? ranges[ranges.length - 1].end : '09:00';
    const fallbackEnd = this.suggestEndTime(fallbackStart);

    this.scheduleDraft[day] = [...ranges, { start: fallbackStart, end: fallbackEnd }];
  }

  removeRange(day: number, index: number): void {
    this.scheduleDraft[day] = this.getRangesForDay(day).filter((_, idx) => idx !== index);
  }

  getDayValidationError(day: number): string {
    const ranges = this.getRangesForDay(day);

    for (const range of ranges) {
      if (!range.start || !range.end) {
        return 'ADMIN.SITES.CALENDARS.SCHEDULE.ERROR.REQUIRED';
      }

      if (this.toMinutes(range.start) >= this.toMinutes(range.end)) {
        return 'ADMIN.SITES.CALENDARS.SCHEDULE.ERROR.INVALID_RANGE';
      }
    }

    const sorted = [...ranges].sort(
      (left, right) => this.toMinutes(left.start) - this.toMinutes(right.start),
    );
    for (let i = 1; i < sorted.length; i += 1) {
      if (this.toMinutes(sorted[i].start) < this.toMinutes(sorted[i - 1].end)) {
        return 'ADMIN.SITES.CALENDARS.SCHEDULE.ERROR.OVERLAP';
      }
    }

    return '';
  }

  saveScheduleDraft(): void {
    if (!this.calendarId) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('COMMON.INFO'),
        detail: this.translate.instant('ADMIN.SITES.CALENDARS.SCHEDULE.ERROR.SAVE_FIRST'),
      });
      return;
    }

    if (this.hasScheduleErrors()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('COMMON.ERROR'),
        detail: this.translate.instant('ADMIN.SITES.CALENDARS.SCHEDULE.ERROR.SAVE'),
      });
      return;
    }

    const timetables = this.buildTimetablesPayload();
    this.calendarService
      .replaceTimetables(this.customersId, this.sitesId, this.calendarId, {
        timetables,
      })
      .subscribe({
        next: (rows: any) => {
          if (this.calendar) {
            this.calendar.timetables = rows || [];
          }
          this.hydrateScheduleDraft();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('ADMIN.SITES.CALENDARS.SCHEDULE.INFO.SUMMARY'),
            detail: this.translate.instant('ADMIN.SITES.CALENDARS.SCHEDULE.INFO.DETAIL'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.translate.instant('ADMIN.SITES.CALENDARS.SCHEDULE.ERROR.SAVE'),
          });
        },
      });
  }

  hasScheduleErrors(): boolean {
    return this.weekDays.some((day) => !!this.getDayValidationError(day.id));
  }

  private hydrateScheduleDraft(): void {
    this.scheduleDraft = this.createEmptyScheduleDraft();

    const timetables = this.calendar?.timetables || [];
    for (const timetable of timetables) {
      if (!timetable.active) {
        continue;
      }
      const day = Number(timetable.weekdays_id);
      if (!Number.isFinite(day) || day < 1 || day > 7) {
        continue;
      }

      const start = `${String(timetable.start_hour).padStart(2, '0')}:${String(
        timetable.start_minute,
      ).padStart(2, '0')}`;
      const end = `${String(timetable.end_hour).padStart(2, '0')}:${String(
        timetable.end_minute,
      ).padStart(2, '0')}`;
      this.scheduleDraft[day] = [...this.getRangesForDay(day), { start, end }];
    }
    this.loading = false;
  }

  private createEmptyScheduleDraft(): Record<number, Array<{ start: string; end: string }>> {
    return {
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
      7: [],
    };
  }

  private buildTimetablesPayload(): Array<{
    weekdays_id: number;
    start_hour: number;
    start_minute: number;
    end_hour: number;
    end_minute: number;
    active: number;
  }> {
    const entries: Array<{
      weekdays_id: number;
      start_hour: number;
      start_minute: number;
      end_hour: number;
      end_minute: number;
      active: number;
    }> = [];

    Object.entries(this.scheduleDraft).forEach(([weekday, ranges]) => {
      const weekdays_id = Number(weekday);
      (ranges || []).forEach((range) => {
        const [start_hour, start_minute] = (range.start || '00:00')
          .split(':')
          .map((value) => Number(value));
        const [end_hour, end_minute] = (range.end || '00:00')
          .split(':')
          .map((value) => Number(value));
        entries.push({
          weekdays_id,
          start_hour,
          start_minute,
          end_hour,
          end_minute,
          active: 1,
        });
      });
    });
    return entries;
  }

  private loadTimetables(): void {
    if (!this.calendarId) {
      this.hydrateScheduleDraft();
      return;
    }

    this.calendarService
      .getTimetables(this.customersId, this.sitesId, this.calendarId)
      .subscribe({
        next: (timetables: any[]) => {
          if (this.calendar) {
            this.calendar.timetables = timetables || [];
          }
          this.hydrateScheduleDraft();
        },
        error: () => {
          this.hydrateScheduleDraft();
        },
      });
  }

  private toMinutes(value: string): number {
    const [hours, minutes] = (value || '').split(':').map((part) => +part);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return -1;
    }
    return hours * 60 + minutes;
  }

  private suggestEndTime(startTime: string): string {
    const start = this.toMinutes(startTime);
    if (start < 0) {
      return '10:00';
    }

    const end = Math.min(start + 240, 23 * 60 + 59);
    const hours = String(Math.floor(end / 60)).padStart(2, '0');
    const minutes = String(end % 60).padStart(2, '0');
    return `${hours}:${minutes}`;
  }
}
